// Contact profile photos: validation, storage paths and the in-browser resize
// that keeps every stored photo small (see
// supabase/migrations/20261019120000_contact_avatars.sql).

export const CONTACT_AVATAR_BUCKET = "contact-avatars";
export const CONTACT_AVATAR_TYPES = ["image/png", "image/jpeg", "image/webp"];
/** Before resizing. The stored file is a small JPEG. */
export const CONTACT_AVATAR_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const CONTACT_AVATAR_SIZE = 512;
/** Signed photo links last an hour; cached a little less. */
export const CONTACT_AVATAR_URL_TTL_SECONDS = 60 * 60;

export function contactAvatarFileError(file: { type: string; size: number }): string | null {
  if (!CONTACT_AVATAR_TYPES.includes(file.type)) {
    return "Choose a PNG, JPG or WebP image";
  }
  if (file.size > CONTACT_AVATAR_MAX_UPLOAD_BYTES) {
    return "Choose an image under 10MB";
  }
  return null;
}

export function contactAvatarPath(businessId: string, id: string = crypto.randomUUID()) {
  return `${businessId}/${id}.jpg`;
}

/** Centre-crop to a square and scale down to at most 512px, as a JPEG. */
export async function resizeContactAvatar(file: File, size = CONTACT_AVATAR_SIZE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const target = Math.min(size, side);
    const canvas = document.createElement("canvas");
    canvas.width = target;
    canvas.height = target;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser can't prepare the photo");
    context.fillStyle = "#ffffff"; // transparent PNGs get a white background
    context.fillRect(0, 0, target, target);
    context.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      target,
      target,
    );
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("This browser can't prepare the photo"))),
        "image/jpeg",
        0.85,
      ),
    );
  } finally {
    bitmap.close();
  }
}
